import { useMemo } from 'react';
import { Sheet, SheetContent, SheetTitle } from '@/shared/widgets/sheet';
import { formatDisplayPrice } from '@/shared/utils/priceFormatter';
import { usePopupHistory } from '@/shared/hooks/usePopupHistory';
import { cn } from '@/shared/utils/cn';
import type { CustomizationSelection } from '../../domain/models/CustomizationSelection';
import type { ParsedCustomizationOptions } from '../../domain/models/CustomizationOptions';
import type { GoldPurityOption } from '../../domain/models/CustomizationOptions';
import { CustomizationOptionRow } from './CustomizationOptionRow';

interface CustomizationSheetProps {
  open: boolean;
  designName: string;
  draft: CustomizationSelection;
  parsedOptions: ParsedCustomizationOptions;
  goldPurities: GoldPurityOption[];
  previewPrice: number | null;
  isPreviewLoading: boolean;
  isPriceAnimating: boolean;
  isApplying: boolean;
  onClose: () => void;
  onOptionChange: (key: string, value: string | number) => void;
  onApply: () => void;
  onCancel: () => void;
}

export function CustomizationSheet({
  open,
  designName,
  draft,
  parsedOptions,
  goldPurities,
  previewPrice,
  isPreviewLoading,
  isPriceAnimating,
  isApplying,
  onClose,
  onOptionChange,
  onApply,
  onCancel,
}: CustomizationSheetProps) {
  usePopupHistory({ isOpen: open, onClose, stateKey: 'customizationSheet' });

  const solitaireQualityActive =
    draft.solitaire_quality ??
    (draft.solitaire_clarity && draft.solitaire_color
      ? `${draft.solitaire_clarity}_${draft.solitaire_color}`
      : undefined);

  const firstRowKey = useMemo(() => {
    if (parsedOptions.goldColors.length > 0) return 'goldColor';
    if (goldPurities.length > 0) return 'goldPurity';
    if (!parsedOptions.isSolitaire && parsedOptions.diamonds.length > 0) return 'diamond';
    if (parsedOptions.isSolitaire && parsedOptions.solitaireCarats.length > 0) return 'solitaireCarat';
    if (parsedOptions.isSolitaire && parsedOptions.solitaireClarities.length > 0) return 'solitaireClarity';
    if (parsedOptions.isSolitaire && parsedOptions.solitaireColors.length > 0) return 'solitaireColor';
    if (parsedOptions.isSolitaire && parsedOptions.solitaireQualities.length > 0) return 'solitaireQuality';
    return null;
  }, [parsedOptions, goldPurities]);

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onCancel(); }}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="flex h-auto flex-col gap-0 p-0"
      >
        <div className="shrink-0 border-b border-[#e2e2e2] px-[15px] pb-[11px] pt-[17px] text-center">
          <SheetTitle className="text-[17px] font-semibold leading-none tracking-[0.015em] text-black">
            Customize Design
          </SheetTitle>
        </div>

        <div className="shrink-0 border-b border-[#e2e2e2] px-[15px] pb-[17px] pt-[15px]">
          <div className="flex items-center justify-between">
            <p className="flex-1 text-base font-normal leading-snug text-black">{designName}</p>
            <p
              className={cn(
                'shrink-0 text-[18px] font-normal leading-snug text-black',
                isPreviewLoading && !isPriceAnimating && 'opacity-60',
                isPriceAnimating && 'animate-customization-price-bounce'
              )}
            >
              {previewPrice != null ? formatDisplayPrice(previewPrice) : '—'}
            </p>
          </div>
        </div>

        <div className="shrink-0">
          {parsedOptions.goldColors.length > 0 && (
            <CustomizationOptionRow
              label="Gold Color"
              isFirst={firstRowKey === 'goldColor'}
              options={parsedOptions.goldColors.map((option) => ({
                value: option.color,
                label: option.displayName,
              }))}
              activeValue={draft.metal_color}
              onSelect={(value) => onOptionChange('metal_color', value)}
            />
          )}

          {goldPurities.length > 0 && (
            <CustomizationOptionRow
              label="Gold Purity"
              isFirst={firstRowKey === 'goldPurity'}
              options={goldPurities.map((option) => ({
                value: option.metalPurity,
                label: option.metalPurity,
              }))}
              activeValue={draft.metal_karatage}
              onSelect={(value) => onOptionChange('metal_karatage', value)}
            />
          )}

          {!parsedOptions.isSolitaire && parsedOptions.diamonds.length > 0 && (
            <CustomizationOptionRow
              label="Diamond"
              isFirst={firstRowKey === 'diamond'}
              options={parsedOptions.diamonds.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              activeValue={draft.diamond_type}
              onSelect={(value) => onOptionChange('diamond_type', value)}
            />
          )}

          {parsedOptions.isSolitaire && parsedOptions.solitaireCarats.length > 0 && (
            <CustomizationOptionRow
              label="Solitaire Carat"
              isFirst={firstRowKey === 'solitaireCarat'}
              options={parsedOptions.solitaireCarats.map((option) => ({
                value: String(option.weight),
                label: String(option.weight),
              }))}
              activeValue={draft.solitaire_carat}
              onSelect={(value) => onOptionChange('solitaire_carat', value)}
            />
          )}

          {parsedOptions.isSolitaire && parsedOptions.solitaireClarities.length > 0 && (
            <CustomizationOptionRow
              label="Solitaire Clarity"
              isFirst={firstRowKey === 'solitaireClarity'}
              options={parsedOptions.solitaireClarities.map((option) => ({
                value: option.name,
                label: option.name,
              }))}
              activeValue={draft.solitaire_clarity}
              onSelect={(value) => onOptionChange('solitaire_clarity', value)}
            />
          )}

          {parsedOptions.isSolitaire && parsedOptions.solitaireColors.length > 0 && (
            <CustomizationOptionRow
              label="Solitaire Color"
              isFirst={firstRowKey === 'solitaireColor'}
              options={parsedOptions.solitaireColors.map((option) => ({
                value: option.name,
                label: option.name,
              }))}
              activeValue={draft.solitaire_color}
              onSelect={(value) => onOptionChange('solitaire_color', value)}
            />
          )}

          {parsedOptions.isSolitaire && parsedOptions.solitaireQualities.length > 0 && (
            <CustomizationOptionRow
              label="Solitaire Quality"
              isFirst={firstRowKey === 'solitaireQuality'}
              options={parsedOptions.solitaireQualities.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              activeValue={solitaireQualityActive}
              onSelect={(value) => onOptionChange('solitaire_quality', value)}
            />
          )}
        </div>

        <div className="flex shrink-0 border-t border-[#e2e2e2]">
          <button
            type="button"
            className="flex-1 py-3.5 text-sm font-medium uppercase text-[#808080]"
            onClick={onCancel}
            disabled={isApplying}
          >
            Cancel
          </button>
          <span className="w-px bg-[#e2e2e2]" aria-hidden />
          <button
            type="button"
            className="flex-1 py-3.5 text-sm font-medium uppercase text-[var(--link-blue)] disabled:opacity-50"
            onClick={onApply}
            disabled={isApplying || isPreviewLoading}
          >
            Apply
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
